import { LogoMark } from "@/components/Logo";
import { COMPANY } from "@shared/company-info";
import type { ReactNode } from "react";

const WRAP = "max-w-[880px] mx-auto px-5 md:px-8";

export default function LegalPageLayout({
  title,
  subtitle,
  lastUpdated,
  children,
}: {
  title: string;
  subtitle?: string;
  lastUpdated?: string;
  children: ReactNode;
}) {
  return (
    <div
      className="min-h-screen"
      style={{
        background: "var(--brand-bone)",
        color: "var(--brand-ink)",
        fontFamily: "var(--font-sans)",
      }}
    >
      <div
        className="sticky top-0 z-30 backdrop-blur"
        style={{
          background: "color-mix(in oklab, var(--brand-bone) 92%, transparent)",
          borderBottom: "1px solid var(--brand-line)",
        }}
      >
        <div className={`${WRAP} flex items-center justify-between py-4`}>
          <a
            href="/"
            className="flex items-center gap-2.5 no-underline"
            style={{ color: "var(--brand-teal)" }}
          >
            <LogoMark size={26} />
            <span
              className="font-display font-semibold text-[16px] tracking-[-0.02em]"
              style={{ color: "var(--brand-ink)" }}
            >
              Lead{" "}
              <span className="font-normal" style={{ color: "var(--brand-fog)" }}>
                Conversa
              </span>
            </span>
          </a>
          <a
            href="/"
            className="text-[13px] no-underline hover:underline"
            style={{ color: "var(--brand-fog)" }}
          >
            ← Voltar
          </a>
        </div>
      </div>

      <main className={`${WRAP} py-12 md:py-16`}>
        <header className="mb-10">
          <h1
            className="font-display font-semibold text-[32px] md:text-[40px] tracking-[-0.02em] leading-tight m-0"
            style={{ color: "var(--brand-ink)" }}
          >
            {title}
          </h1>
          {subtitle && (
            <p
              className="text-[15px] leading-[1.55] mt-3"
              style={{ color: "var(--brand-fog)" }}
            >
              {subtitle}
            </p>
          )}
          {lastUpdated && (
            <p
              className="font-mono text-[11px] tracking-[0.08em] uppercase mt-4"
              style={{ color: "var(--brand-fog)" }}
            >
              Última atualização: {lastUpdated}
            </p>
          )}
        </header>

        <article className="legal-content">{children}</article>

        <div
          className="mt-16 pt-8 text-[13px] leading-[1.6]"
          style={{ borderTop: "1px solid var(--brand-line)", color: "var(--brand-fog)" }}
        >
          <p className="m-0">
            <strong style={{ color: "var(--brand-ink)" }}>{COMPANY.legalEntity}</strong> — CNPJ{" "}
            {COMPANY.cnpj}
          </p>
          <p className="m-0 mt-1">{COMPANY.addressFormatted}</p>
          <p className="m-0 mt-1">
            Contato:{" "}
            <a
              href={`mailto:${COMPANY.dpoEmail}`}
              style={{ color: "var(--brand-teal)" }}
            >
              {COMPANY.dpoEmail}
            </a>
          </p>
        </div>
      </main>

      <footer
        className="py-7 mt-8"
        style={{ borderTop: "1px solid var(--brand-line)" }}
      >
        <div
          className={`${WRAP} flex justify-between flex-wrap gap-2 font-mono text-[11px] tracking-[0.08em]`}
          style={{ color: "var(--brand-fog)" }}
        >
          <span>
            © {new Date().getFullYear()} {COMPANY.brandName} · Todos os direitos reservados
          </span>
          <span className="flex gap-4">
            <a href="/privacidade" className="no-underline" style={{ color: "var(--brand-fog)" }}>
              Privacidade
            </a>
            <a href="/termos" className="no-underline" style={{ color: "var(--brand-fog)" }}>
              Termos
            </a>
            <a href="/lgpd" className="no-underline" style={{ color: "var(--brand-fog)" }}>
              LGPD
            </a>
          </span>
        </div>
      </footer>

      <style>{`
        .legal-content h2 {
          font-family: var(--font-display, inherit);
          font-size: 22px;
          font-weight: 600;
          letter-spacing: -0.01em;
          color: var(--brand-ink);
          margin: 2.5rem 0 0.75rem;
        }
        .legal-content h3 {
          font-size: 16px;
          font-weight: 600;
          color: var(--brand-ink);
          margin: 1.5rem 0 0.5rem;
        }
        .legal-content p {
          font-size: 15px;
          line-height: 1.7;
          color: var(--brand-ink);
          margin: 0 0 1rem;
        }
        .legal-content ul {
          font-size: 15px;
          line-height: 1.7;
          color: var(--brand-ink);
          padding-left: 1.25rem;
          margin: 0 0 1rem;
        }
        .legal-content li { margin-bottom: 0.4rem; }
        .legal-content a { color: var(--brand-teal); }
        .legal-content strong { color: var(--brand-ink); }
      `}</style>
    </div>
  );
}
